
import { Options, Vue } from 'vue-class-component';
import { CompanyGarmentCompositionClient } from '@/services/Services';
import InfoModal from '@/components/modals/infoModal.vue';
import store from '@/store';
import * as OM from '@/Model';
import * as VM from '@/viewModel';

@Options({
    components: {}
})
export default class CompositionEditor extends Vue {

    // Same tolerance as the server; the epsilon absorbs binary floating point error
    // so that a total of 99.99 is accepted exactly as the backend accepts it.
    static readonly tolerance = 0.01 + 1e-9;

    garmentIdentifier: string = "";
    subType: string = "";

    compositions: OM.FootwearComposition[] = [];
    serverErrors: OM.CompositionValidationError[] = [];

    loaded: boolean = false;

    created() {
        this.garmentIdentifier = this.$route.params.garmentIdentifier.toString();
        this.subType = decodeURIComponent(this.$route.params.subType.toString());
        this.load(false);
    }

    get isReadOnly() {
        return store.state.loggedUser.role == VM.Roles.ReadOnly;
    }

    load(edmondPreset: boolean) {
        CompanyGarmentCompositionClient.getComposition(this.garmentIdentifier, this.subType, edmondPreset)
        .then(x => {
            this.compositions = x.compositions;
            this.serverErrors = [];
            this.loaded = true;
        })
    }

    get allNames(): string[] {
        let names: string[] = [];
        this.compositions.forEach(macro => {
            names.push((macro.name || "").trim().toLowerCase());
            (macro.insideComposition || []).forEach(child => names.push((child.name || "").trim().toLowerCase()));
        })
        return names;
    }

    isDuplicate(name: string): boolean {
        let normalized = (name || "").trim().toLowerCase();
        if(!normalized)
            return false;
        return this.allNames.filter(x => x == normalized).length > 1;
    }

    isNameInvalid(name: string): boolean {
        let trimmed = (name || "").trim();
        return !trimmed || trimmed.includes("--") || this.isDuplicate(name);
    }

    childrenSum(macro: OM.FootwearComposition): number {
        return (macro.insideComposition || []).reduce((sum, child) => sum + (child.percentage || 0), 0);
    }

    childrenOk(macro: OM.FootwearComposition): boolean {
        if((macro.insideComposition || []).length == 0)
            return false;
        return Math.abs(this.childrenSum(macro) - (macro.percentage || 0)) <= CompositionEditor.tolerance;
    }

    get macroTotal(): number {
        return this.compositions.reduce((sum, macro) => sum + (macro.percentage || 0), 0);
    }

    get totalOk(): boolean {
        return Math.abs(this.macroTotal - 100) <= CompositionEditor.tolerance;
    }

    // Composition-wide sum of every sub-element's percentage, across all macros. Per-macro
    // tolerances stack, so this is not implied by totalOk + childrenOk(macro) and must be
    // checked on its own, mirroring the same rule enforced server-side.
    get childrenTotal(): number {
        return this.compositions.reduce((sum, macro) => sum + this.childrenSum(macro), 0);
    }

    get childrenTotalOk(): boolean {
        return Math.abs(this.childrenTotal - 100) <= CompositionEditor.tolerance;
    }

    get globalErrors(): string[] {
        return this.serverErrors.filter(x => !x.name).map(x => x.message);
    }

    rowErrors(name: string): string[] {
        let trimmed = (name || "").trim();
        if(!trimmed)
            return [];
        return this.serverErrors.filter(x => x.name == trimmed).map(x => x.message);
    }

    percentageOk(node: OM.FootwearComposition): boolean {
        return node.percentage > 0 && node.percentage <= 100;
    }

    get isValid(): boolean {
        if(this.compositions.length == 0)
            return false;
        if(!this.totalOk)
            return false;
        if(!this.childrenTotalOk)
            return false;

        return this.compositions.every(macro => {
            if(this.isNameInvalid(macro.name) || !this.percentageOk(macro))
                return false;
            if(!this.childrenOk(macro))
                return false;
            return (macro.insideComposition || []).every(child => !this.isNameInvalid(child.name) && this.percentageOk(child));
        })
    }

    addMacro() {
        let macro = new OM.FootwearComposition();
        this.compositions.push(macro);
    }

    removeMacro(index: number) {
        this.compositions.splice(index, 1);
    }

    addChild(macro: OM.FootwearComposition) {
        if(!macro.insideComposition)
            macro.insideComposition = [];
        macro.insideComposition.push(new OM.FootwearComposition());
    }

    removeChild(macro: OM.FootwearComposition, index: number) {
        macro.insideComposition.splice(index, 1);
    }

    save() {
        let model = new OM.SaveCompanyGarmentCompositionVM();
        model.garmentIdentifier = this.garmentIdentifier;
        model.subType = this.subType;
        model.compositions = this.compositions;

        CompanyGarmentCompositionClient.saveComposition(model)
        .then(() => {
            this.$router.push('/my-compositions');
        })
        .catch(err => {
            this.serverErrors = err && err.errors ? err.errors : [];

            if(this.serverErrors.length == 0) {
                this.$opModal.show(InfoModal, {
                    text: this.$localizationService.getLocalizedValue("Error", "Error"),
                    subtitle: this.$localizationService.getLocalizedValue("Unable to save the composition", "Unable to save the composition"),
                    deny: this.$localizationService.getLocalizedValue("Close", "Close"),
                    denyCb: () => {
                        this.$opModal.closeLast();
                    }
                })
            }
        })
    }
}
